import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  Chip,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TemplateSection, useTemplateBuilder } from './TemplateBuilderContext';

// Styled components
const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'box-shadow 0.2s ease-in-out',
}));

const TemplateSectionsList: React.FC = () => {
  const { templateSections, processingError, tasksState, templateName, templateDescription } =
    useTemplateBuilder();

  const isLoading = tasksState.fileProcessing === 'processing';

  const isProcessingFile = tasksState.fileProcessing === 'processing';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: theme => `1px solid ${theme.palette.divider}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Typography variant="h6" gutterBottom>
        {templateName || 'Generated Sections'}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {templateDescription ||
          'The following sections are going to be included in the template. In order to see them and modify, please select a transcript.'}
      </Typography>

      {processingError && (
        <Box sx={{ mb: 3 }}>
          <Typography color="error" variant="body2">
            {processingError}
          </Typography>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {isProcessingFile ? 'Processing file...' : 'Analyzing transcript...'}
            </Typography>
          </Box>
        )}

        {!isLoading && (
          <List sx={{ width: '100%' }} disablePadding>
            {templateSections.map(section => (
              <ListItem key={section.module_id} disablePadding sx={{ mb: 2 }}>
                <SectionPaper elevation={0} sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {section.public_name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {section.public_description}
                  </Typography>
                </SectionPaper>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Paper>
  );
};

export default TemplateSectionsList;
